import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import { Save } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectPreference } from "../../preferences/preferencesSlice";
import {
  changeEmployeeStatus,
  selectProcesses,
} from "../processSlices";
import { Employee, MedicalStatus } from "../types/processTypes";
import Toast from "../../../components/Layout/Toast";
import MedicalPhoto from "./MedicalPhoto";
import { MedicalFields, MedicalValue } from "./MedicalFields";

interface Props {
  currentEmployee?: Employee;
  handleDialogClose: Function;
  showCoc?: boolean;
}

export const MedicalDialog = ({
  currentEmployee,
  handleDialogClose,
  showCoc = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { locale } = useAppSelector(selectPreference);
  const { selectedEmployee, error: saveFailed } =
    useAppSelector(selectProcesses);

  const employee = selectedEmployee ?? currentEmployee;

  const [value, setValue] = useState<MedicalValue>({
    medicalStatus: employee?.medicalStatus ?? MedicalStatus.not_done,
    medicalBookedDate: employee?.medicalBookedDate,
    medicalExpiryDate: employee?.medicalExpiryDate,
    cocCompleted: employee?.cocCompleted ?? false,
  });

  const handleSave = async () => {
    if (!employee?._id || !employee.status) return;
    const result = await dispatch(
      changeEmployeeStatus({
        _id: employee._id,
        status: employee.status,
        medicalStatus: value.medicalStatus,
        medicalBookedDate: value.medicalBookedDate as Date | undefined,
        medicalExpiryDate: value.medicalExpiryDate as Date | undefined,
        ...(showCoc ? { cocCompleted: value.cocCompleted } : {}),
      }),
    );
    if (changeEmployeeStatus.fulfilled.match(result)) {
      handleDialogClose(true);
    }
  };

  return (
    <DialogContent sx={{ width: "100%", boxSizing: "border-box", pt: 1 }}>
      <Stack spacing={2.5} sx={{ pt: 1, width: "100%", alignItems: "stretch" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
            borderRadius: 2,
            bgcolor: "action.hover",
          }}
        >
          <MedicalPhoto />
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <MedicalFields
            value={value}
            onChange={(patch) => setValue((v) => ({ ...v, ...patch }))}
            locale={locale}
            showCoc={showCoc}
          />
        </Box>
        {saveFailed && (
          <Box sx={{ width: "100%" }}>
            <Toast severity="error">{saveFailed.message}</Toast>
          </Box>
        )}
        <Button
          fullWidth
          size="large"
          variant="contained"
          color="secondary"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
        >
          {t("save")}
        </Button>
      </Stack>
    </DialogContent>
  );
};
